import React, { useState, useRef } from 'react';
import { validateFiles } from '../../utils/fileValidation';
import './DocumentUploader.css';

const DocumentUploader = ({ onUpload, onClose, uploadedDocs, onRemoveDoc }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    handleFileSelection(files);
  };

  // Handle file input change
  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    handleFileSelection(files);
  };

  // Process selected files
  const handleFileSelection = (files) => {
    const { valid, errors } = validateFiles(files);
    
    if (errors.length > 0) {
      alert(`File validation errors:\n${errors.join('\n')}`);
    }

    if (valid.length > 0) {
      setSelectedFiles([...selectedFiles, ...valid]);
    }
  };

  // Remove file from selection
  const removeSelectedFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  // Upload files
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      await onUpload(selectedFiles);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Clear selection after successful upload
      setTimeout(() => {
        setSelectedFiles([]);
        setUploadProgress(0);
        setIsUploading(false);
      }, 1000);
    } catch (error) {
      console.error('Upload error:', error);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="document-uploader">
      <div className="uploader-header">
        <h3>Upload Legal Documents</h3>
        <button className="close-btn" onClick={onClose}>✕</button>
      </div>

      {/* Drag and Drop Zone */}
      <div 
        className={`upload-zone ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
        
        <div className="upload-zone-content">
          <div className="upload-icon">📄</div>
          <p className="upload-text">
            Drag and drop files here, or{' '}
            <button 
              className="browse-link"
              onClick={() => fileInputRef.current?.click()}
            >
              browse
            </button>
          </p>
          <p className="upload-hint">
            Supported: PDF, DOC, DOCX, TXT (Max 10MB each)
          </p>
        </div>
      </div>

      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <div className="selected-files">
          <h4>Selected Files ({selectedFiles.length})</h4>
          <div className="files-list">
            {selectedFiles.map((file, index) => (
              <div key={index} className="file-item">
                <div className="file-icon">📎</div>
                <div className="file-info">
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">{formatFileSize(file.size)}</span>
                </div>
                <button 
                  className="remove-file-btn"
                  onClick={() => removeSelectedFile(index)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span className="progress-text">{uploadProgress}%</span>
            </div>
          )}

          {/* Upload Button */}
          <button 
            className="upload-btn"
            onClick={handleUpload}
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : `Upload ${selectedFiles.length} File(s)`}
          </button>
        </div>
      )}

      {/* Already Uploaded Documents */}
      {uploadedDocs.length > 0 && (
        <div className="uploaded-documents">
          <h4>Uploaded Documents ({uploadedDocs.length})</h4>
          <div className="files-list">
            {uploadedDocs.map((doc) => (
              <div key={doc.id} className="file-item uploaded">
                <div className="file-icon">✓</div>
                <div className="file-info">
                  <span className="file-name">{doc.name}</span>
                  <span className="file-size">{formatFileSize(doc.size)}</span>
                </div>
                <button 
                  className="remove-file-btn"
                  onClick={() => onRemoveDoc(doc.id)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUploader;
